'use client';

import { arrayToCSV, downloadCSV } from '@/lib/csv';

interface ResultsTableProps {
  data: unknown[];
  reportId: string;
}

// Columns to hide in UI (IDs, internal keys). mode is hidden when mode_display exists.
const HIDDEN_COLUMNS: Record<string, string[]> = {
  shipment_profitability: ['shipment_analysis_id', 'invoice_id', 'frid', 'mode'],
  shipment_profitability_list: ['shipment_analysis_id', 'invoice_id', 'frid', 'mode'],
};

// Display names for columns
const COLUMN_LABELS: Record<string, string> = {
  mode_display: 'Mode',
  file_number: 'File Number',
  master_number: 'Master Number',
  total_freight_collected: 'Total Freight Collected',
  freight_charged_cost: 'Freight Charged (Cost)',
  gross_spread: 'Gross Spread',
  gross_margin_pct: 'Gross Margin %',
  net_after_commission: 'Net After Commission',
  true_margin_pct: 'True Margin %',
  prepaid_invoices_ready: 'Prepaid Invoices Ready',
  shipment_type: 'Shipment Type',
  total_processing_hours: 'Time to Receive (hrs)',
  packages_per_hour: 'Packages per Hour',
};

function getDisplayHeaders(data: unknown[], reportId: string): string[] {
  const rawHeaders = Object.keys(data[0] as Record<string, unknown>);
  const hidden = HIDDEN_COLUMNS[reportId] ?? [];
  return rawHeaders.filter((h) => !hidden.includes(h));
}

function getColumnLabel(header: string): string {
  return COLUMN_LABELS[header] ?? header.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ResultsTable({ data, reportId }: ResultsTableProps) {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  const handleExportCSV = () => {
    const filtered = data.map((row) => {
      const r = row as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      headers.forEach((h) => {
        out[getColumnLabel(h)] = r[h];
      });
      return out;
    });
    const csv = arrayToCSV(filtered);
    const filename = `${reportId}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, filename);
  };

  const headers = getDisplayHeaders(data, reportId);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Results ({data.length} rows)
        </h3>
        <button
          onClick={handleExportCSV}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {getColumnLabel(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {headers.map((header) => {
                  const value = (row as Record<string, unknown>)[header];
                  return (
                    <td
                      key={header}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                    >
                      {value !== null && value !== undefined ? String(value) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

